import { FC, useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    CardActionArea,
    useTheme,
    Paper,
    Chip,
    LinearProgress,
    Breadcrumbs,
    Link
} from '@mui/material';
import {
    Apps,
    Security,
    People,
    VpnKey,
    Mail,
    CleaningServices,
    TrendingUp,
    Warning,
    Inbox,
    Message
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PRESENCE_ACTIVE_WINDOW_SECONDS } from '@ids/shared';
import { adminApiClient } from '../utils/adminApiClient';

interface DashboardStats {
    totalClients: number;
    totalUsers: number;
    totalRoles: number;
    totalApiKeys: number;
    pendingInvitations: number;
    uniqueLoggedInUsers: number;
    activeUsers: number;
    sessionCount: number;
    activeWindowSeconds: number;
    systemHealth: 'healthy' | 'warning' | 'error';
    healthDetails?: any;
}

const dashboardCards = [
    { title: 'Client Management', description: 'Verwalten Sie Client-Anwendungen', icon: <Apps sx={{ fontSize: 40 }} />, path: '/app/admin/clients' },
    { title: 'Role Management', description: 'Globale und client-spezifische Rollen', icon: <Security sx={{ fontSize: 40 }} />, path: '/app/admin/roles' },
    { title: 'User Management', description: 'Benutzer verwalten und privilegieren', icon: <People sx={{ fontSize: 40 }} />, path: '/app/admin/users' },
    { title: 'API Keys', description: 'API-Schlüssel für Clients', icon: <VpnKey sx={{ fontSize: 40 }} />, path: '/app/admin/api-keys' },
    { title: 'Invitations', description: 'Einladungen verwalten', icon: <Mail sx={{ fontSize: 40 }} />, path: '/app/admin/invitations' },
    { title: 'Posteingang', description: 'Nachrichten an tintoa-Administratoren', icon: <Inbox sx={{ fontSize: 40 }} />, path: '/app/admin/inbox' },
    { title: 'Nachrichten senden', description: 'Nachricht an einen Nutzer senden', icon: <Message sx={{ fontSize: 40 }} />, path: '/app/admin/messages' },
    { title: 'Data Cleanup', description: 'Datenbereinigung und Wartung', icon: <CleaningServices sx={{ fontSize: 40 }} />, path: '/app/admin/data-cleanup' },
];

export const AdminDashboard: FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const [stats, setStats] = useState<DashboardStats>({
        totalClients: 0,
        totalUsers: 0,
        totalRoles: 0,
        totalApiKeys: 0,
        pendingInvitations: 0,
        uniqueLoggedInUsers: 0,
        activeUsers: 0,
        sessionCount: 0,
        activeWindowSeconds: PRESENCE_ACTIVE_WINDOW_SECONDS,
        systemHealth: 'healthy'
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadDashboardStats = async () => {
            try {
                const dashboardStats = await adminApiClient.getDashboardStats();
                setStats({
                    ...dashboardStats,
                    systemHealth: dashboardStats.systemHealth as 'healthy' | 'warning' | 'error'
                });
            } catch (error) {
                console.error('Failed to load dashboard stats:', error);
                // Fallback to default values on error
                setStats({
                    totalClients: 0,
                    totalUsers: 0,
                    totalRoles: 0,
                    totalApiKeys: 0,
                    pendingInvitations: 0,
                    uniqueLoggedInUsers: 0,
                    activeUsers: 0,
                    sessionCount: 0,
                    activeWindowSeconds: PRESENCE_ACTIVE_WINDOW_SECONDS,
                    systemHealth: 'error'
                });
            } finally {
                setLoading(false);
            }
        };

        loadDashboardStats();
    }, []);

    const handleCardClick = (path: string) => {
        navigate(path);
    };

    const getHealthColor = (health: string) => {
        switch (health) {
            case 'healthy': return 'success';
            case 'warning': return 'warning';
            case 'error': return 'error';
            default: return 'default';
        }
    };

    const getHealthIcon = (health: string) => {
        switch (health) {
            case 'healthy': return <TrendingUp />;
            case 'warning': return <Warning />;
            case 'error': return <Warning />;
            default: return <TrendingUp />;
        }
    };

    const getComponentDisplayName = (component: string) => {
        switch (component) {
            case 'mongodb': return 'MongoDB';
            case 'redis': return 'Redis';
            case 'build_info': return 'Build Info';
            default: return component;
        }
    };

    const getRedisStatusLabel = (status: any) => {
        if (status?.status === 'up') return 'Online';
        const rs = status?.redisStatus;
        if (rs === 'disabled') return 'Deaktiviert';
        if (rs === 'reconnecting') return 'Wiederverbinden…';
        if (rs === 'error') return 'Fehler';
        return 'Offline';
    };

    const getSystemStatusMessage = (health: string, healthDetails: any) => {
        switch (health) {
            case 'healthy':
                return 'Alle Systeme funktionieren normal. Keine kritischen Probleme gemeldet.';
            case 'warning':
                const warningComponents = Object.entries(healthDetails.details)
                    .filter(([_, s]: [string, any]) => s?.status === 'down' || s?.status === 'error')
                    .map(([component]) => getComponentDisplayName(component));
                return `Warnung: ${warningComponents.join(', ')} ${warningComponents.length === 1 ? 'hat' : 'haben'} Probleme.`;
            case 'error':
                return 'Kritische Systemfehler erkannt. Bitte überprüfen Sie die Systemkomponenten.';
            default:
                return 'System-Status unbekannt.';
        }
    };

    if (loading) {
        return (
            <Box sx={{ width: '100%' }}>
                <LinearProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Breadcrumb */}
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link 
                    color="inherit" 
                    href="/app/profile" 
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/app/profile');
                    }}
                    sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                    Profil
                </Link>
                <Typography color="text.primary">Admin Dashboard</Typography>
            </Breadcrumbs>

            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Admin Dashboard
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Willkommen im Administrationsbereich. Hier können Sie alle Aspekte des Identity Service verwalten.
                </Typography>
            </Box>

            {/* System Health Status */}
            <Paper 
                variant="subtle"
                sx={{ 
                    p: 3, 
                    mb: 4
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {getHealthIcon(stats.systemHealth)}
                    <Typography variant="h6">System Status</Typography>
                    <Chip 
                        label={
                            stats.systemHealth === 'healthy' ? 'Gesund' : 
                            stats.systemHealth === 'warning' ? 'Warnung' : 'Fehler'
                        } 
                        color={getHealthColor(stats.systemHealth)}
                        size="small"
                    />
                </Box>
                
                {stats.healthDetails ? (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {getSystemStatusMessage(stats.systemHealth, stats.healthDetails)}
                        </Typography>
                        
                        {/* Component Status Details */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                            {Object.entries(stats.healthDetails.details).map(([component, status]: [string, any]) => (
                                <Box key={component} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip 
                                        label={getComponentDisplayName(component)}
                                        size="small"
                                        color={status?.status === 'up' ? 'success' : 'error'}
                                        variant="outlined"
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {component === 'redis'
                                            ? getRedisStatusLabel(status)
                                            : (status?.status === 'up' ? 'Online' : 'Offline')}
                                        {component === 'build_info' && status?.version && ` (v${status.version})`}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                        
                        {/* Build Information */}
                        {stats.healthDetails.details?.build_info && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Build Information
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1 }}>
                                    {stats.healthDetails.details.build_info.version && (
                                        <Typography variant="caption">
                                            <strong>Version:</strong> {stats.healthDetails.details.build_info.version}
                                        </Typography>
                                    )}
                                    {stats.healthDetails.details.build_info.build_number && (
                                        <Typography variant="caption">
                                            <strong>Build:</strong> #{stats.healthDetails.details.build_info.build_number}
                                        </Typography>
                                    )}
                                    {stats.healthDetails.details.build_info.build_branch && (
                                        <Typography variant="caption">
                                            <strong>Branch:</strong> {stats.healthDetails.details.build_info.build_branch}
                                        </Typography>
                                    )}
                                    {stats.healthDetails.details.build_info.git_hash && (
                                        <Typography variant="caption">
                                            <strong>Commit:</strong> {stats.healthDetails.details.build_info.git_hash.substring(0, 8)}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        System-Status wird geladen...
                    </Typography>
                )}
            </Paper>

            {/* Quick Stats */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
                <Paper 
                    variant="subtle"
                    sx={{ 
                        p: 2, 
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="h4" color="secondary">
                        {stats.totalClients}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Client-Anwendungen
                    </Typography>
                </Paper>
                <Paper 
                    variant="subtle"
                    sx={{ 
                        p: 2, 
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="h4" color="secondary">
                        {stats.totalUsers}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Benutzer
                    </Typography>
                </Paper>
                <Paper 
                    variant="subtle"
                    sx={{ 
                        p: 2, 
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="h4" color="secondary">
                        {stats.uniqueLoggedInUsers}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Eingeloggte User
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {stats.sessionCount} {stats.sessionCount === 1 ? 'Session' : 'Sessions'}
                    </Typography>
                </Paper>
                <Paper 
                    variant="subtle"
                    sx={{ 
                        p: 2, 
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="h4" color="secondary">
                        {stats.activeUsers}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Gerade aktiv
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        letzte {Math.max(1, Math.round((stats.activeWindowSeconds || PRESENCE_ACTIVE_WINDOW_SECONDS) / 60))} Min.
                    </Typography>
                </Paper>
                <Paper 
                    variant="subtle"
                    sx={{ 
                        p: 2, 
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="h4" color="secondary">
                        {stats.totalRoles}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Rollen
                    </Typography>
                </Paper>
                <Paper 
                    variant="subtle"
                    sx={{ 
                        p: 2, 
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="h4" color="secondary">
                        {stats.pendingInvitations}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Ausstehende Einladungen
                    </Typography>
                </Paper>
            </Box>

            {/* Admin Function Cards */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
                Verwaltungsfunktionen
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                {dashboardCards.map((card, index) => (
                    <Card 
                        key={card.title}
                        variant="subtle"
                        sx={{ 
                            height: '100%',
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: theme.shadows[8],
                            }
                        }}
                    >
                        <CardActionArea 
                            onClick={() => handleCardClick(card.path)}
                            sx={{ height: '100%', p: 3 }}
                        >
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Box sx={{ color: theme.palette.semantic!.chartColors[index % theme.palette.semantic!.chartColors.length], mb: 2 }}>
                                    {card.icon}
                                </Box>
                                <Typography variant="h6" component="h3" gutterBottom>
                                    {card.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {card.description}
                                </Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                ))}
            </Box>
        </Box>
    );
};
